import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { authApi } from "../api/axios";
import { useAuth } from "../auth/useAuth";
import { setAmplitudeUser, trackEvent } from "../analytics/amplitude";
import DesktopShowcase from "../components/DesktopShowcase";
import FooterLinks from "../components/FooterLinks";
import MobileTestimonial from "../components/MobileTestimonial";

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;

const TESTIMONIALS = [
  {
    text: "A user friendly B2B platform for all your global flight ticketing needs Globally competitive airfares from 600+ Airlines including 150+ LCCs covering 175+ Countries.",
    image: `${import.meta.env.PUBLIC_CLOUDFRONT_BASE_URL}/assets/api-img.png`,
    name: "B2B Platform",
    title: "CoreFares",
    ratings: 3.5,
  },
  {
    text: "Best-in-class Global Air Fares for Business, Leisure and Group Travel Customers from leading Travel Management Companies, Tour Operators, Global MICE Companies and Online Travel Agencies.",
    image: `${import.meta.env.PUBLIC_CLOUDFRONT_BASE_URL}/assets/api-img.png`,
    name: "B2B Platform",
    title: "CoreFares",
    ratings: 4,
  },
  {
    text: "Multi-currency payment options, Customer branded e-tickets, 24x7 support for ticketing and post-ticketing services.",
    image: `${import.meta.env.PUBLIC_CLOUDFRONT_BASE_URL}/assets/api-img.png`,
    name: "B2B Platform",
    title: "CoreFares",
    ratings: 3.5,
  },
  {
    text: "Industry Standard compliant API that allows Customers to seamlessly integrate and consume competitive flight content from 600+ Airlines including 150+ LCCs covering 175+ Countries.",
    image: `${import.meta.env.PUBLIC_CLOUDFRONT_BASE_URL}/assets/api-img.png`,
    name: "API",
    title: "CoreFares",
    ratings: 5,
  },
  {
    text: " Easy to implement White Label solution enabling Customers to set up their online presence. Available to customers who wish to extend CoreFares global flight content to their customers.",
    image: `${import.meta.env.PUBLIC_CLOUDFRONT_BASE_URL}/assets/api-img.png`,
    name: "White Label",
    title: "CoreFares",
    ratings: 5,
  },
];

function AuthLayout() {
  const [showPassword, setShowPassword] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(TESTIMONIALS.length);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [loginType, setLoginType] = useState("client");
  const [loading, setLoading] = useState(false);
  const { loginFromResponse } = useAuth();
  const navigate = useNavigate();

  const infiniteTestimonials = useMemo(
    () => [...TESTIMONIALS, ...TESTIMONIALS, ...TESTIMONIALS],
    [],
  );
  const activeIndex =
    ((currentIndex % TESTIMONIALS.length) + TESTIMONIALS.length) %
    TESTIMONIALS.length;
  const currentTestimonial = useMemo(
    () => TESTIMONIALS[activeIndex],
    [activeIndex],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => prev + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (currentIndex >= TESTIMONIALS.length * 2) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(TESTIMONIALS.length);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [currentIndex]);

  const prevTestimonial = () =>
    setCurrentIndex((i) =>
      i - 1 < TESTIMONIALS.length ? i - 1 + TESTIMONIALS.length : i - 1,
    );
  const nextTestimonial = () => setCurrentIndex((i) => i + 1);

  const handleEmailBlur = (e) => {
    const value = e.target.value.trim();
    if (value && !EMAIL_REGEX.test(value)) {
      toast.error("Please enter a valid email address!");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const email = e.target.email.value.trim();
    const password = e.target.password.value.trim();
    const internalUser = loginType === "staff";
    const clientId = e.target.clientId?.value?.trim();

    if (!email) return toast.error("Please enter your email address!");
    if (!EMAIL_REGEX.test(email))
      return toast.error("Please enter a valid email address!");
    if (!password) return toast.error("Please enter your password!");
    if (password.length < 6)
      return toast.error("Password must be at least 6 characters long!");
    if (!internalUser && !clientId)
      return toast.error("Customer ID is required for client login!");
    if (!internalUser && clientId.length < 3)
      return toast.error("Please enter a valid Customer ID!");

    const payload = {
      UserName: email,
      Password: password,
      InternalUser: internalUser,
      ...(internalUser ? {} : { OTPAccountNumber: clientId }),
    };

    setLoading(true);
    try {
      const { data } = await authApi.post("/account/login", payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (data.success && data.requiresOtp) {
        localStorage.setItem("otpUserId", data.userId);
        toast.info(data?.message);
        navigate("/verify-otp");
        return;
      }

      if (data?.success && data?.isFirstTimeLogin) {
        const redirect = data?.redirect;
        if (typeof redirect === "string" && redirect.trim()) {
          navigate(redirect.trim(), { replace: true });
        } else {
          navigate("/reset-password", { replace: true });
        }
        return;
      }

      if (data.success) {
        loginFromResponse?.(data);
        setAmplitudeUser({ ...(data.userProfile || {}), email });
        trackEvent("Login", {
          loginType,
          email,
        });
        navigate("/search");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-100">
      <div className="flex flex-col md:flex-row w-full md:min-h-screen bg-white overflow-hidden">
        <DesktopShowcase
          testimonials={infiniteTestimonials}
          currentIndex={currentIndex}
          isTransitioning={isTransitioning}
        />

        <div className="md:w-[45%] p-6 sm:p-8 md:p-10 flex flex-col">
          <div className="text-center mb-2">
            <img
              src={`${import.meta.env.PUBLIC_CLOUDFRONT_BASE_URL}/assets/corefares-logo.svg`}
              alt="CoreFares Logo"
              className="h-24 sm:h-28 mx-auto mb-4"
            />
          </div>

          <Outlet
            context={{
              showPassword,
              setShowPassword,
              loginType,
              setLoginType,
              loading,
              handleEmailBlur,
              handleLogin,
            }}
          />

          <div className="mt-auto pt-6 hidden md:block">
            <FooterLinks className="text-xs text-center text-gray-500 space-x-6" />
          </div>
        </div>

        <div className="md:hidden px-4 py-3">
          <FooterLinks className="text-xs text-center text-gray-700 space-x-3" />
        </div>

        <MobileTestimonial
          currentTestimonial={currentTestimonial}
          onPrev={prevTestimonial}
          onNext={nextTestimonial}
          currentIndex={activeIndex}
          total={TESTIMONIALS.length}
          onDotClick={(i) => setCurrentIndex(TESTIMONIALS.length + i)}
        />
      </div>
    </div>
  );
}

export default AuthLayout;
